import {
  View, Text, StyleSheet, ScrollView, ActivityIndicator,
  TouchableOpacity, Alert,
} from 'react-native';
import { useRouter, useNavigation } from 'expo-router';
import { useState, useEffect } from 'react';
import { Ionicons } from '@expo/vector-icons';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { useAuth } from '@/hooks/useAuth';
import { useFinancialOverview } from '@/hooks/useFinancialOverview';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { FinancialDesktop } from '@/components/FinancialDesktop';
import { PaymentStatus, StudentPaymentStatus } from '@/lib/types';

const STATUS_COLOR: Record<PaymentStatus, string> = {
  paid: '#2E7D32',
  pending: '#F9A825',
  overdue: '#C8102E',
  no_fee: '#888',
};

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Pago',
  pending: 'Pendente',
  overdue: 'Vencido',
  no_fee: 'Avulso',
};

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

function formatCurrency(value: number): string {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StudentRow({ item, onPress }: { item: StudentPaymentStatus; onPress: () => void }) {
  const { student, status, currentMonthPayment } = item;
  return (
    <TouchableOpacity style={styles.row} onPress={onPress} activeOpacity={0.7}>
      <View style={[styles.dot, { backgroundColor: STATUS_COLOR[status] }]} />
      <View style={styles.rowInfo}>
        <Text style={styles.rowName}>{student.full_name ?? student.email}</Text>
        {student.monthly_fee != null && (
          <Text style={styles.rowSub}>
            {formatCurrency(student.monthly_fee)}/mês · vence dia {student.due_day ?? 10}
          </Text>
        )}
      </View>
      <View style={styles.rowRight}>
        <Text style={[styles.rowStatus, { color: STATUS_COLOR[status] }]}>
          {STATUS_LABEL[status]}
        </Text>
        {currentMonthPayment && (
          <Text style={styles.rowDate}>
            {new Date(currentMonthPayment.paid_at).toLocaleDateString('pt-BR', {
              day: '2-digit', month: '2-digit',
            })}
          </Text>
        )}
      </View>
      <Ionicons name="chevron-forward" size={16} color="#ccc" />
    </TouchableOpacity>
  );
}

export default function FinancesScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { profile } = useAuth();
  const isDesktop = useIsDesktop();
  const now = new Date();

  // Em desktop, troca pelo layout admin (tabela + KPIs + filtros)
  if (isDesktop) return <FinancialDesktop personalId={profile?.id ?? ''} />;

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [exporting, setExporting] = useState(false);

  const isCurrentMonth =
    selectedYear === now.getFullYear() && selectedMonth === now.getMonth();

  const { overview, projected, received, pendingAmount, loading } =
    useFinancialOverview(profile?.id ?? '', { year: selectedYear, month: selectedMonth });

  function goToPrevMonth() {
    if (selectedMonth === 0) {
      setSelectedYear(y => y - 1);
      setSelectedMonth(11);
    } else {
      setSelectedMonth(m => m - 1);
    }
  }

  function goToNextMonth() {
    if (isCurrentMonth) return;
    if (selectedMonth === 11) {
      setSelectedYear(y => y + 1);
      setSelectedMonth(0);
    } else {
      setSelectedMonth(m => m + 1);
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const paid = overview.filter(o => o.status === 'paid');
      const rows = paid.map(o => `
        <tr>
          <td>${o.student.full_name ?? o.student.email}</td>
          <td>R$ ${(o.currentMonthPayment?.amount ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
          <td>${o.currentMonthPayment
            ? new Date(o.currentMonthPayment.paid_at).toLocaleDateString('pt-BR')
            : '—'}</td>
          <td>${o.currentMonthPayment?.method ?? '—'}</td>
        </tr>`).join('');

      const html = `
        <html><head><meta charset="utf-8"/>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #1A1A1A; }
          h1 { color: #C8102E; font-size: 20px; margin-bottom: 4px; }
          .subtitle { color: #888; font-size: 13px; margin-bottom: 20px; }
          .summary { display: flex; gap: 16px; margin-bottom: 20px; }
          .sum-box { border: 1px solid #eee; border-radius: 8px; padding: 12px 16px; }
          .sum-label { font-size: 11px; color: #888; text-transform: uppercase; }
          .sum-val { font-size: 18px; font-weight: bold; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #f5f5f5; text-align: left; padding: 8px; border-bottom: 2px solid #ddd; }
          td { padding: 8px; border-bottom: 1px solid #eee; }
          .total { font-weight: bold; font-size: 15px; margin-top: 16px; text-align: right; color: #2E7D32; }
        </style></head>
        <body>
          <h1>Relatório Financeiro</h1>
          <div class="subtitle">${profile?.full_name ?? ''} · ${MONTH_NAMES[selectedMonth]} ${selectedYear}</div>
          <div class="summary">
            <div class="sum-box">
              <div class="sum-label">Recebido</div>
              <div class="sum-val" style="color:#2E7D32">R$ ${received.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div class="sum-box">
              <div class="sum-label">Pendente</div>
              <div class="sum-val" style="color:#F9A825">R$ ${pendingAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div class="sum-box">
              <div class="sum-label">Previsto</div>
              <div class="sum-val">R$ ${projected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
          <table>
            <thead><tr><th>Aluno</th><th>Valor</th><th>Data</th><th>Método</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="4" style="color:#aaa">Nenhum pagamento registrado</td></tr>'}</tbody>
          </table>
          <div class="total">Total recebido: R$ ${received.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
        </body></html>`;

      const { uri } = await Print.printToFileAsync({ html });
      await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: 'Exportar relatório' });
    } catch {
      Alert.alert('Erro', 'Não foi possível gerar o PDF.');
    } finally {
      setExporting(false);
    }
  }

  const paid = overview.filter(o => o.status === 'paid');
  const pending = overview.filter(o => o.status === 'pending');
  const overdue = overview.filter(o => o.status === 'overdue');

  const today = now.getDate();
  const upcomingAlerts = isCurrentMonth
    ? overview.filter(o => {
        if (o.status !== 'pending') return false;
        const dueDay = o.student.due_day ?? 10;
        return dueDay >= today && dueDay <= today + 5;
      })
    : [];

  const pctReceived = projected > 0 ? Math.round((received / projected) * 100) : 0;

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          style={[styles.pdfBtn, exporting && { opacity: 0.5 }]}
          onPress={handleExportPDF}
          disabled={exporting}
        >
          <Ionicons name="download-outline" size={14} color="#C8102E" />
          <Text style={styles.pdfBtnText}>{exporting ? 'Gerando...' : 'PDF'}</Text>
        </TouchableOpacity>
      ),
    });
  }, [exporting]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color="#C8102E" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Month navigator */}
      <View style={styles.monthNav}>
        <TouchableOpacity onPress={goToPrevMonth} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <Ionicons name="chevron-back" size={22} color="#C8102E" />
        </TouchableOpacity>
        <Text style={styles.monthLabel}>
          {MONTH_NAMES[selectedMonth]} {selectedYear}
        </Text>
        <TouchableOpacity
          onPress={goToNextMonth}
          disabled={isCurrentMonth}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Ionicons name="chevron-forward" size={22} color={isCurrentMonth ? '#ddd' : '#C8102E'} />
        </TouchableOpacity>
      </View>

      {/* Revenue bar */}
      <View style={styles.revenueCard}>
        <Text style={styles.revenueLabel}>Receita do mês</Text>
        <View style={styles.revenueValues}>
          <View style={styles.rv}>
            <Text style={[styles.rvVal, { color: '#2E7D32' }]}>{formatCurrency(received)}</Text>
            <Text style={styles.rvLbl}>Recebido</Text>
          </View>
          <View style={styles.rvDivider} />
          <View style={styles.rv}>
            <Text style={[styles.rvVal, { color: '#F9A825' }]}>{formatCurrency(pendingAmount)}</Text>
            <Text style={styles.rvLbl}>Pendente</Text>
          </View>
          <View style={styles.rvDivider} />
          <View style={styles.rv}>
            <Text style={styles.rvVal}>{formatCurrency(projected)}</Text>
            <Text style={styles.rvLbl}>Previsto</Text>
          </View>
        </View>
        {projected > 0 && (
          <>
            <View style={styles.progressTrack}>
              <View style={[styles.progressFill, { width: `${Math.min(pctReceived, 100)}%` as any }]} />
            </View>
            <Text style={styles.progressPct}>{pctReceived}% recebido</Text>
          </>
        )}
      </View>

      {/* Upcoming alerts */}
      {upcomingAlerts.length > 0 && (
        <View style={styles.alertBox}>
          <Text style={styles.alertTitle}>⚠ Vencem nos próximos 5 dias</Text>
          {upcomingAlerts.map(o => (
            <View key={o.student.id} style={styles.alertRow}>
              <Text style={styles.alertName}>{o.student.full_name ?? o.student.email}</Text>
              <Text style={styles.alertSub}>
                dia {o.student.due_day ?? 10} · {formatCurrency(o.student.monthly_fee ?? 0)}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Overdue alerts */}
      {overdue.length > 0 && (
        <View style={styles.overdueBox}>
          <Text style={styles.overdueTitle}>🔴 Vencidos</Text>
          {overdue.map(o => {
            const daysLate = o.student.due_day
              ? Math.floor(
                  (now.getTime() - new Date(selectedYear, selectedMonth, o.student.due_day).getTime()) /
                    (1000 * 60 * 60 * 24),
                )
              : null;
            return (
              <View key={o.student.id} style={styles.alertRow}>
                <Text style={styles.alertName}>{o.student.full_name ?? o.student.email}</Text>
                <Text style={[styles.alertSub, { color: '#C8102E' }]}>
                  {daysLate != null && daysLate > 0 ? `${daysLate} dias em atraso · ` : ''}
                  {formatCurrency(o.student.monthly_fee ?? 0)}
                </Text>
              </View>
            );
          })}
        </View>
      )}

      {/* Summary cards */}
      <View style={styles.summary}>
        <View style={[styles.summaryCard, { backgroundColor: '#0d2a1a' }]}>
          <Text style={[styles.summaryValue, { color: '#4caf50' }]}>{paid.length}</Text>
          <Text style={styles.summaryLabel}>Pagos</Text>
        </View>
        <View style={[styles.summaryCard, { backgroundColor: '#2a1a0d' }]}>
          <Text style={[styles.summaryValue, { color: '#F9A825' }]}>{pending.length}</Text>
          <Text style={styles.summaryLabel}>Pendentes</Text>
        </View>
        <View style={[styles.summaryCard, { backgroundColor: '#2a0d0d' }]}>
          <Text style={[styles.summaryValue, { color: '#C8102E' }]}>{overdue.length}</Text>
          <Text style={styles.summaryLabel}>Vencidos</Text>
        </View>
      </View>

      {/* Student list */}
      <Text style={styles.listLabel}>Todos os alunos</Text>
      {overview.length === 0 ? (
        <Text style={styles.empty}>Nenhum aluno ativo</Text>
      ) : (
        overview.map(item => (
          <StudentRow
            key={item.student.id}
            item={item}
            onPress={() => router.push(`/(personal)/students/${item.student.id}` as any)}
          />
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 16, paddingBottom: 40 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 22, fontWeight: '700', color: '#1A1A1A' },
  pdfBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1.5, borderColor: '#C8102E', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5 },
  pdfBtnText: { fontSize: 12, color: '#C8102E', fontWeight: '600' },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', borderRadius: 12, paddingHorizontal: 16, paddingVertical: 12, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  monthLabel: { fontSize: 16, fontWeight: '700', color: '#1A1A1A' },
  revenueCard: { backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  revenueLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 },
  revenueValues: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  rv: { flex: 1, alignItems: 'center' },
  rvVal: { fontSize: 16, fontWeight: '700', color: '#1A1A1A' },
  rvLbl: { fontSize: 10, color: '#888', marginTop: 2 },
  rvDivider: { width: 1, height: 30, backgroundColor: '#eee' },
  progressTrack: { height: 6, backgroundColor: '#eee', borderRadius: 6, overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: '#2E7D32', borderRadius: 6 },
  progressPct: { fontSize: 10, color: '#888', textAlign: 'right', marginTop: 4 },
  alertBox: { backgroundColor: '#FFF8E1', borderWidth: 1, borderColor: '#FFE082', borderRadius: 12, padding: 12, marginBottom: 10 },
  alertTitle: { fontSize: 12, fontWeight: '700', color: '#856404', marginBottom: 6 },
  alertRow: { paddingVertical: 4, borderTopWidth: 1, borderTopColor: '#FFE082' },
  alertName: { fontSize: 12, fontWeight: '600', color: '#1A1A1A' },
  alertSub: { fontSize: 11, color: '#856404', marginTop: 1 },
  overdueBox: { backgroundColor: '#FFEBEE', borderWidth: 1, borderColor: '#FFCDD2', borderRadius: 12, padding: 12, marginBottom: 10 },
  overdueTitle: { fontSize: 12, fontWeight: '700', color: '#B71C1C', marginBottom: 6 },
  summary: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  summaryCard: { flex: 1, backgroundColor: '#1a1a1a', borderRadius: 10, padding: 10, alignItems: 'center' },
  summaryValue: { fontSize: 20, fontWeight: '700' },
  summaryLabel: { fontSize: 9, color: '#aaa', marginTop: 2 },
  listLabel: { fontSize: 11, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#fff', borderRadius: 10, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  dot: { width: 10, height: 10, borderRadius: 5 },
  rowInfo: { flex: 1 },
  rowName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  rowSub: { fontSize: 11, color: '#888', marginTop: 2 },
  rowRight: { alignItems: 'flex-end' },
  rowStatus: { fontSize: 12, fontWeight: '700' },
  rowDate: { fontSize: 10, color: '#888', marginTop: 1 },
  empty: { textAlign: 'center', color: '#888', marginTop: 40 },
});
